const BASE_URL = 'https://toxsee.onrender.com';
const STORAGE_KEY = 'toxsee_symptoms';

function normalizeUsername(username) {
  return String(username || '').trim().toLowerCase();
}

function getLocal() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function setLocal(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function getLocalForUser(username) {
  const normalized = normalizeUsername(username);
  return getLocal().filter(
    s => normalizeUsername(s.username) === normalized
  );
}

export async function fetchSymptoms(username) {
  const normalized = normalizeUsername(username);
  try {
    const res = await fetch(
      `${BASE_URL}/api/symptoms/${encodeURIComponent(normalized)}`
    );
    if (res.ok) {
      const { symptoms } = await res.json();
      const others = getLocal().filter(
        s => normalizeUsername(s.username) !== normalized
      );
      setLocal([...(symptoms || []), ...others]);
      return symptoms;
    }
  } catch {
    // network unavailable — fall through to local cache
  }
  return getLocalForUser(normalized);
}

export async function logSymptom(username, entry) {
  const normalized = normalizeUsername(username);
  const localEntry = {
    _id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    username: normalized,
    name: entry.name,
    note: entry.note || '',
    severity: entry.severity,
    loggedAt: entry.loggedAt || new Date().toISOString(),
  };

  try {
    const res = await fetch(`${BASE_URL}/api/symptoms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: normalized, ...entry }),
    });
    if (res.ok) {
      const { symptom } = await res.json();
      setLocal([symptom, ...getLocal()]);
      return symptom;
    }
  } catch {
    // backend unavailable — save locally only
  }

  setLocal([localEntry, ...getLocal()]);
  return localEntry;
}

export async function deleteSymptom(id) {
  const idStr = String(id);
  setLocal(getLocal().filter(s => String(s._id) !== idStr));

  if (!idStr.startsWith('local-') && !idStr.startsWith('mock-')) {
    try {
      await fetch(`${BASE_URL}/api/symptoms/${idStr}`, { method: 'DELETE' });
    } catch {
      // silent — already removed locally
    }
  }
}

// ── Mock data ─────────────────────────────────────────────────────────────────
const MOCK_NAMES = [
  'Fatigue', 'Nausea', 'Peripheral neuropathy', 'Pain', 'Appetite loss',
  'Mouth sores', 'Constipation', 'Diarrhea', 'Headache', 'Insomnia',
  'Shortness of breath', 'Rash', 'Dizziness', 'Muscle weakness', 'Hair loss',
  'Vomiting', 'Chills', 'Swelling', 'Anxiety', 'Cold sensitivity',
];

const MOCK_SEVERITIES = [
  'mild', 'mild', 'mild',
  'moderate', 'moderate', 'moderate',
  'severe',
  'very_severe',
];

export function seedMockData(username) {
  const normalized = normalizeUsername(username);
  if (getLocalForUser(normalized).length > 0) return;

  const now = new Date();
  const entries = Array.from({ length: 24 }, (_, i) => {
    const loggedAt = new Date(now);
    loggedAt.setDate(loggedAt.getDate() - Math.floor(Math.random() * 30));
    loggedAt.setHours(Math.floor(Math.random() * 16) + 7);
    loggedAt.setMinutes(Math.floor(Math.random() * 60));
    return {
      _id: `mock-${i}-${Date.now()}`,
      username: normalized,
      name: MOCK_NAMES[Math.floor(Math.random() * MOCK_NAMES.length)],
      severity: MOCK_SEVERITIES[Math.floor(Math.random() * MOCK_SEVERITIES.length)],
      note: '',
      loggedAt: loggedAt.toISOString(),
    };
  });

  const others = getLocal().filter(
    s => normalizeUsername(s.username) !== normalized
  );
  const sorted = entries.sort((a, b) => new Date(b.loggedAt) - new Date(a.loggedAt));
  setLocal([...sorted, ...others]);
}
